import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { themeStorage } from '../utils/userStorage';

// Utility function to set CSS custom properties from theme mapping
const setCSSFromTheme = (themeMapping: ThemeMapping): void => {
  const root = document.documentElement;

  // Set page background from bg-primary-css if available, otherwise use bg-primary
  if (themeMapping['bg-primary-css']) {
    root.style.setProperty('--page-bg', themeMapping['bg-primary-css']);
  } else if (themeMapping['bg-primary']) {
    // Fallback: extract color from Tailwind class
    const tailwindClass = themeMapping['bg-primary'];

    // Create a temporary element to get the computed color
    const tempEl = document.createElement('div');
    tempEl.className = tailwindClass;
    tempEl.style.visibility = 'hidden';
    tempEl.style.position = 'absolute';
    tempEl.style.pointerEvents = 'none';
    document.body.appendChild(tempEl);

    const computedColor = window.getComputedStyle(tempEl).backgroundColor;
    document.body.removeChild(tempEl);

    root.style.setProperty('--page-bg', computedColor);
  }
};

export type Theme = 'light' | 'dark'; // Supported theme types

export interface ThemeMapping {
  [semanticName: string]: string; // semantic name -> Tailwind class
}

export interface ThemeRegistry {
  [theme: string]: ThemeMapping; // theme -> semantic mappings
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  provideTailwind: (semanticName: string) => string;
  registerTheme: (theme: Theme, mappings: ThemeMapping) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: Theme;
  initialRegistry?: ThemeRegistry;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialTheme = 'dark',
  initialRegistry = {},
}) => {
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const [registry, setRegistry] = useState<ThemeRegistry>(initialRegistry);

  // Load theme from persistent storage on mount
  useEffect(() => {
    const savedTheme = themeStorage.get();
    if (savedTheme) {
      setThemeState(savedTheme);
    } else {
      // Check system preference if no saved theme
      if (typeof window !== 'undefined' && window.matchMedia) {
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)',
        ).matches;
        const systemTheme: Theme = prefersDark ? 'dark' : 'light';
        setThemeState(systemTheme);
        themeStorage.set(systemTheme);
      }
    }
  }, []);

  // Persist theme changes
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    themeStorage.set(newTheme);
  };

  // Update CSS custom property when theme changes
  React.useEffect(() => {
    const themeMapping = registry[theme];

    if (themeMapping) {
      setCSSFromTheme(themeMapping);
    }
  }, [theme, registry]);

  const registerTheme = React.useCallback(
    (themeName: Theme, mappings: ThemeMapping) => {
      setRegistry((prev) => ({
        ...prev,
        [themeName]: { ...prev[themeName], ...mappings },
      }));
    },
    [],
  );

  const provideTailwind = React.useCallback(
    (semanticName: string): string => {
      const themeMapping = registry[theme];
      if (!themeMapping) {
        console.warn(`No theme mapping found for theme: ${theme}`);
        return '';
      }

      const className = themeMapping[semanticName];
      if (!className) {
        console.warn(
          `No class mapping found for semantic name: ${semanticName} in theme: ${theme}`,
        );
        return '';
      }

      return className;
    },
    [theme, registry],
  );

  const contextValue = useMemo(
    () => ({
      theme,
      setTheme,
      provideTailwind,
      registerTheme,
    }),
    [theme, setTheme, provideTailwind, registerTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
